import React, {useCallback, useEffect, useEffectEvent, useState} from 'react';
import {useNavigate} from 'react-router';
import {
  ActiveMergeSuggestionTypes,
  type MergeSuggestion,
  type MergeSuggestionType,
} from 'services/mergeSuggestions/types';
import {useLocalization} from 'localization';
import {
  mapMergeSuggestionsToQueueItem,
  useSetMergeSuggestionQueue,
} from 'services/mergeSuggestions';
import {useGetMergeSuggestionsQuery} from 'services/mergeSuggestions/mergeSuggestionsApi';
import {
  mergeSuggestionShowUserCreatedUpdated,
  mergeSuggestionTypeUpdated,
  selectMergeSuggestionCurrentType,
  selectMergeSuggestionShowUserCreated,
} from 'services/settings';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {CheckboxField, FlexBox, Scene, Select} from 'components';
import {Table} from 'schema';
import {useMergeSuggestionSchema} from './hooks/useMergeSuggestionSchema';
import {useMergeSuggestionTableConfiguration} from './hooks/useMergeSuggestionTableConfiguration';
import {usePopulateMergeSuggestionTitles} from './hooks/usePopulateMergeSuggestionTitles';

export const MergeSuggestionsSceneCore: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {t} = useLocalization();
  const configuration = useMergeSuggestionTableConfiguration();
  const mergeSuggestionSchema = useMergeSuggestionSchema();
  const setQueue = useSetMergeSuggestionQueue();

  const type = useAppSelector(selectMergeSuggestionCurrentType);
  const setType = useCallback(
    (type: MergeSuggestionType) => {
      dispatch(mergeSuggestionTypeUpdated(type));
    },
    [dispatch],
  );

  const userCreated = useAppSelector(selectMergeSuggestionShowUserCreated);
  const setUserCreated = useCallback(
    (value: boolean) => {
      dispatch(mergeSuggestionShowUserCreatedUpdated(value));
    },
    [dispatch],
  );

  const {data, isFetching} = useGetMergeSuggestionsQuery({
    type,
    userCreated,
  });

  // Add titles to source and target.
  const populateMergeSuggestionsWithTitles =
    usePopulateMergeSuggestionTitles(type);
  const [dataWithTitles, setDataWithTitles] = useState<MergeSuggestion[]>([]);

  const setData = useEffectEvent(setDataWithTitles);
  useEffect(() => {
    if (data) {
      setData(data);
      populateMergeSuggestionsWithTitles(data).then(dataWithTitles => {
        setData(dataWithTitles);
      });
    }
  }, [data, populateMergeSuggestionsWithTitles]);

  return (
    <Scene browserTitle={t('appbar.mergeOverview')}>
      <Scene.Header title={t('appbar.mergeOverview')} />
      <Scene.Content>
        <FlexBox horizontal mb={2} mt={2} gap={2} width={'500px'}>
          <Select
            options={ActiveMergeSuggestionTypes.map(type => ({
              value: type,
              label: t(`page.mergeSuggestion.type.${type}.label`),
            }))}
            onChange={setType}
            value={type}
          />
          <CheckboxField
            onChange={value => setUserCreated(value)}
            value={userCreated}
            fieldId={'userCreated'}
            name={'userCreated'}
            boxLabel={t('form.global.field.userCreated.label')}
            layout={{
              width: '400px',
            }}
          />
        </FlexBox>
        <FlexBox width={'1400px'}>
          <Table
            schema={mergeSuggestionSchema}
            data={dataWithTitles}
            header={null}
            loading={isFetching}
            configuration={configuration}
            onRowClick={row => {
              // @todo Increase beyond current pagination page size.
              setQueue(
                dataWithTitles
                  // Exclude clicked item
                  .filter(s => s.id !== row.id)
                  .map(mapMergeSuggestionsToQueueItem),
              );
              if (row.type === 'SERIES') {
                navigate(`/series/${row.sourceId}/merge/${row.targetId}`);
              } else if (row.type === 'WORK') {
                navigate(`/work/${row.sourceId}/merge/${row.targetId}`);
              } else if (row.type === 'AGENT') {
                navigate(`/agent/${row.sourceId}/merge/${row.targetId}`);
              }
            }}
          />
        </FlexBox>
      </Scene.Content>
    </Scene>
  );
};
